import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import {
  Card,
  CardHeader,
  Col,
  Container,
  Row,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";

import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";

import { CardBody } from "reactstrap";
import FormInput from "../../../components/FormInput";
import CreatableSelect from "react-select/creatable";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import moment from "moment";
import {
  createUnit,
  createVat,
  getAllUnits,
  getAllVats,
  createProduct,
  updateProduct,
  deleteProduct,
  deleteSelectedProducts,
} from "../../../api";
// validations
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

import { getProduct } from "../../../api";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSelector";

interface OptionType {
  label: string;
  value: string;
}

interface FormDataType {
  selectedInvoiceTypeValue: string;
  productOptions: OptionType[];
  serviceOptions: OptionType[];
  isDiscountBasicAmount: boolean;
  isAddDiscount: boolean;
}

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control custom-datepicker"
        value={value}
        onClick={onClick}
        ref={ref}
        placeholder=""
        readOnly
        style={{ cursor: "pointer" }}
      />
      <span
        className="input-group-text bg-white"
        style={{ cursor: "pointer" }}
        onClick={onClick}
      >
        <FaCalendarAlt />
      </span>
    </div>
  ),
);

const NewProductPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const showSnackbar = useSnackbar();
  const [unitOptions, setUnitOptions] = useState<OptionType[]>([
    { value: "1", label: "pieces" },
    { value: "2", label: "kilogram" },
    { value: "3", label: "boxes" },
    { value: "4", label: "meter" },
    { value: "5", label: "kilometer" },
    { value: "6", label: "litre" },
    { value: "7", label: "pallets" },
    { value: "8", label: "sets" },
    { value: "9", label: "units" },
    { value: "10", label: "hours" },
    { value: "11", label: "days" },
    { value: "12", label: "weeks" },
    { value: "13", label: "units" },
    { value: "14", label: "plans" },
  ]);

  const [vatOptions, setVatOptions] = useState<OptionType[]>([
    { value: "0", label: "0" },
    { value: "7", label: "7" },
    { value: "19", label: "19" },
  ]);

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [selectedVatOption, setSelectedVatOption] = useState<OptionType | null>(
    null,
  );

  const [forms, setForms] = useState<FormDataType[]>([]);
  const [selectedInvoiceTypeValue, setSelectedInvoiceTypeValue] =
    useState<string>("1");
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [formData, setFormData] = useState({
    unit_type: "",
    unit_price: "",
    qty: "",
    delivery_date: "",
    total_cost_gross: "",
    total_cost_net: "",
    description: "",
  });

  const defaultValues: any = {
    item: "",
    unit_price: "",
    qty: "",
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      const payload = {
        productId: id,
      };

      try {
        const productRes = await getProduct(payload);

        if (productRes && productRes.success == true) {
          const productData = productRes?.data;

          reset({
            item: productData?.item,
            service_product_number: productData?.service_product_number,
            unit_price: productData?.unit_price,
            qty: productData?.qty,
          });

          setFormData(prevFormData => ({
            ...prevFormData,
            description: productData?.description,
            unit_price: productData?.unit_price,
            qty: productData?.qty,
          }));

          setSelectedInvoiceTypeValue(productData?.invoice_type);
          setSelectedOption(productData?.unit_type);
          setSelectedVatOption(productData?.vat_type);
          setSelectedDate(productData?.selected_date);
        }
      } catch (error: any) {
        console.log(error);
      }
    };

    fetchProduct();
  }, []);

  useEffect(() => {
    const fetchAllVatsAndUnits = async () => {
      try {
        const vatsResponse = await getAllVats();
        const unitsResponse = await getAllUnits();

        if (unitsResponse.data) {
          const unitOptions = unitsResponse.data.map((unitData: any) => {
            const unitOption = {
              value: unitData.id,
              label: unitData.unit_name,
            };
            return unitOption;
          });

          setUnitOptions(unitOptions);
        }

        if (vatsResponse.data) {
          const vatOptions = vatsResponse.data.map((vatData: any) => {
            const vatOption = { value: vatData.id, label: vatData.vat_name };
            return vatOption;
          });

          setVatOptions(vatOptions);
        }
      } catch (error: any) {
        console.log(error);
      }
    };

    fetchAllVatsAndUnits();
  }, []);

  useEffect(() => {
    let total_cost_net = Number(formData.unit_price) * Number(formData.qty);
    let total_cost_gross =
      Number(total_cost_net) +
      (Number(total_cost_net) * Number(selectedVatOption?.label)) / 100;

    total_cost_net = parseFloat(total_cost_net.toFixed(2));
    total_cost_gross = parseFloat(total_cost_gross.toFixed(2));

    if (total_cost_net && total_cost_gross) {
      setFormData(prevFormData => ({
        ...prevFormData,
        total_cost_gross: total_cost_gross.toFixed(2),
        total_cost_net: total_cost_net.toFixed(2),
      }));
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        total_cost_gross: "",
        total_cost_net: "",
      }));
    }
  }, [formData.unit_price, formData.qty, selectedVatOption]);

  const handleInputChange = (e: any) => {
    const floatValue = parseFloat(e.target.value) || 0;
    setFormData(prevFormData => ({
      ...formData,
      [e.target.name]: floatValue,
    }));
  };

  const resolver = yupResolver(
    yup.object().shape({
      item: yup.string().required("Please Enter Item Name"),
    }),
  );

  const methods = useForm({ defaultValues, resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = methods;

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleChange = (newValue: OptionType | null) => {
    setSelectedOption(newValue);
  };

  const handleInvoiceTypeChange = (selectedInvoiceTypeValue: string) => {
    setSelectedOption(null);
    setSelectedInvoiceTypeValue(selectedInvoiceTypeValue);
  };

  const handleCreateUnit = async (inputValue: string) => {
    const payload = {
      unit_title: inputValue,
    };
    const response = await createUnit(payload);

    if (response.success == true) {
      const newOption = { value: response.unit_id, label: inputValue };
      setUnitOptions(prevOptions => [...prevOptions, newOption]);
      setSelectedOption(newOption);
    }
  };

  const handleVatChange = (newValue: OptionType | null) => {
    setSelectedVatOption(newValue);
  };

  const handleVatCreate = async (inputValue: string) => {
    if (/^\d+(\.\d{1,2})?$/.test(inputValue)) {
      const payload = {
        vat_title: inputValue,
      };
      const response = await createVat(payload);

      if (response.success == true) {
        const newOption = { value: response.vat_id, label: inputValue };

        setVatOptions(prevOptions => [...prevOptions, newOption]);
        setSelectedVatOption(newOption);
      }
    }
  };

  const onSubmitForm = async (values: any) => {
    const payload = {
      ...values,
      description: formData.description,
      invoice_type: selectedInvoiceTypeValue,
      unit_type: selectedOption || "",
      selected_date: selectedDate
        ? moment(selectedDate).format("YYYY-MM-DD")
        : "",
      vat_type: selectedVatOption || "",
      total_cost_net: formData?.total_cost_net,
      total_cost_gross: formData?.total_cost_gross,
    };

    try {
      setLoading(true);
      if (!id) {
        const response = await createProduct(payload);
        if (response.success == false) {
          showSnackbar(response.message, "error");
        } else {
          showSnackbar(response.message, "info");
        }
      } else {
        const updatePayload = {
          ...payload,
          productId: id,
        };
        const updateResponse = await updateProduct(updatePayload);
        if (updateResponse.success == false) {
          showSnackbar(updateResponse.message, "error");
        } else {
          showSnackbar(updateResponse.message, "info");
        }
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);

      showSnackbar(error.message, "error");
    }
  };

  const handleGoToSubscriptionPage = () => {
    navigate("/subscription");
  };

  return (
    <>
      <div className="right-panel">
        <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
          <span className="mb-0 default-text" style={{ fontWeight: "500" }}>
            {t("Products")}
          </span>
          <div className="d-flex gap-2">
            <LanguageSwitcher />
            <Button variant="danger" onClick={handleGoToSubscriptionPage}>
              {t("Unlock Pro")}
            </Button>
            <Button
              variant="secondary"
              onClick={handleSubmit(onSubmitForm)}
              style={{
                fontSize: "0.875rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
              }}
            >
              {id ? t("Edit Product") : "+" + t("Add Product")}
              {loading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </Button>
          </div>
        </div>
        <div className="py-3  px-1 px-md-4">
          <div id="breadcrumb" className="d-flex gap-2 my-3">
            <i className="bx bx-home font-size-22"></i>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 h-5 w-5"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span>
              <a style={{ fontWeight: "500" }}> {t("Products")}</a>
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 h-5 w-5"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span>
              <a style={{ fontWeight: "500" }}>
                {id ? t("Edit Product") : t("New Product")}{" "}
              </a>
            </span>
          </div>
          <Row className="product-card pt-3">
            <Col>
              <Card>
                <CardHeader className="card-header">
                  {id ? t("Edit Product") : t("New Product")}
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleSubmit(onSubmitForm)}>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">
                          {t("Item")}
                          <i className="required-field ms-2">*</i>
                        </span>
                      </Col>
                      <Col md="8">
                        <FormInput
                          type="text"
                          name="item"
                          labelClassName="form-label"
                          className="form-control"
                          register={register}
                          errors={errors}
                          control={control}
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">
                          {t("Service / Product Number")}
                        </span>
                      </Col>
                      <Col md="8">
                        <FormInput
                          type="text"
                          name="service_product_number"
                          labelClassName="form-label"
                          className="form-control"
                          register={register}
                          errors={errors}
                          control={control}
                        />
                      </Col>
                    </Row>

                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">{t("Description")}</span>
                      </Col>
                      <Col md="8">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="description"
                          value={formData?.description}
                          onChange={e =>
                            setFormData(prevFormData => ({
                              ...prevFormData,
                              description: e.target.value,
                            }))
                          }
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">
                          {t("Select Service / Product")}
                        </span>
                      </Col>
                      <Col md="8">
                        <Form.Select
                          value={selectedInvoiceTypeValue}
                          onChange={e =>
                            handleInvoiceTypeChange(e.target.value)
                          }
                          aria-label="Select example"
                        >
                          <option value="1">{t("Service")}</option>
                          <option value="2">{t("Product")}</option>
                        </Form.Select>
                      </Col>
                    </Row>

                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">{t("Unit")}</span>
                      </Col>
                      <Col md="8">
                        <CreatableSelect
                          isClearable
                          options={unitOptions}
                          onChange={handleChange}
                          onCreateOption={handleCreateUnit}
                          value={selectedOption}
                          placeholder={t("Select or create an option...")}
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">{t("Unit Price")}</span>
                      </Col>
                      <Col md="8">
                        <FormInput
                          type="number"
                          name="unit_price"
                          onChange={handleInputChange}
                          labelClassName="form-label"
                          className="form-control"
                          register={register}
                          errors={errors}
                          control={control}
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">{t("Quantity")}</span>
                      </Col>
                      <Col md="8">
                        <FormInput
                          type="number"
                          name="qty"
                          onChange={handleInputChange}
                          labelClassName="form-label"
                          className="form-control"
                          register={register}
                          errors={errors}
                          control={control}
                        />
                      </Col>
                    </Row>
                    {selectedInvoiceTypeValue == "1" ? (
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">
                            {t("Service Date")}{" "}
                          </span>
                        </Col>
                        <Col md="8">
                          <DatePicker
                            selected={selectedDate}
                            onChange={(date: Date | null) =>
                              setSelectedDate(date)
                            }
                            customInput={<CustomInput />}
                            dateFormat="yyyy-MM-dd" // Customize the date format
                            placeholderText="mm / dd / yyyy"
                            className=" custom-datepicker" // Use Bootstrap styling if needed
                          />
                        </Col>
                      </Row>
                    ) : (
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">
                            {t("Product Delivery date")}
                          </span>
                        </Col>
                        <Col md="8">
                          <DatePicker
                            selected={selectedDate}
                            onChange={(date: Date | null) =>
                              setSelectedDate(date)
                            }
                            customInput={<CustomInput />}
                            dateFormat="yyyy-MM-dd" // Customize the date format
                            placeholderText="mm / dd / yyyy"
                            className=" custom-datepicker" // Use Bootstrap styling if needed
                          />
                        </Col>
                      </Row>
                    )}

                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">{t("VAT")} %</span>
                      </Col>
                      <Col md="8">
                        <CreatableSelect
                          isClearable
                          options={vatOptions}
                          onChange={handleVatChange}
                          onCreateOption={handleVatCreate}
                          value={selectedVatOption}
                          placeholder={t("Select or create an option...")}
                          onInputChange={(inputValue, { action }) => {
                            if (action === "input-change") {
                              return inputValue.replace(/[^0-9.]/g, ""); // Allow only numbers and dots
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">
                          {t("Total Cost (NET)")}
                        </span>
                      </Col>
                      <Col md="8">
                        <FormInput
                          type="text"
                          name="total_cost_net"
                          value={formData?.total_cost_net || ""}
                          labelClassName="form-label"
                          className="form-control"
                          readOnly
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col md={4} className="vertical-center">
                        <span className="default-text">
                          {t("Total Cost (GROSS)")}
                        </span>
                      </Col>
                      <Col md="8">
                        <FormInput
                          type="text"
                          name="total_cost_gross"
                          value={formData?.total_cost_gross || ""}
                          labelClassName="form-label"
                          className="form-control"
                          readOnly
                        />
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant="secondary"
                        type="submit"
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {id ? t("Edit Product") : "+" + t("Add Product")}
                        {loading && (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="ms-2"
                          />
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default NewProductPage;
